// Pure scrubbing logic for error tracking. Deliberately has no imports — the
// dialog invite token is a capability URL, so anything leaving the browser
// must have it redacted, and this logic stays unit-testable in isolation.

// Matches the token path segment in /dialog/<token> (SPA route) and
// /d/<token> (public API). A bare "/d" or "/dashboard" never matches.
const TOKEN_PATH = /(\/(?:dialog|d)\/)[^/?#\s"']+/g;

// Member-management endpoints carry addressee emails in query strings
// (?email=...). PM emails are data we hold anyway, but they don't belong
// in an external tracker's URLs, breadcrumbs, or error messages.
const EMAIL_PARAM = /([?&]email=)[^&#\s"']+/gi;

// Headers that carry credentials (bearer/JWT/cookie) the URL/token redactor
// above won't match. sendDefaultPii:false already keeps request headers out of
// Sentry events, so this is defense-in-depth if that ever changes: redact the
// whole value rather than pass a JWT through untouched. Compared lowercased.
const SENSITIVE_HEADERS = new Set(['authorization', 'cookie', 'x-id-token', 'x-api-key']);

export function redactSensitive(text: string): string {
  return text.replace(TOKEN_PATH, '$1[token]').replace(EMAIL_PARAM, '$1[email]');
}

export interface ScrubbableBreadcrumb {
  category?: string;
  message?: string;
  data?: { [key: string]: unknown };
}

export interface ScrubbableEvent {
  message?: string;
  request?: { url?: string; headers?: { [key: string]: unknown } };
  exception?: { values?: Array<{ value?: string }> };
  breadcrumbs?: ScrubbableBreadcrumb[];
}

// Console output can carry respondent free-text; drop those breadcrumbs
// outright rather than trying to filter their contents.
export function scrubBreadcrumb<T extends ScrubbableBreadcrumb>(breadcrumb: T): T | null {
  if (breadcrumb.category === 'console') return null;
  if (breadcrumb.message) breadcrumb.message = redactSensitive(breadcrumb.message);
  for (const [key, value] of Object.entries(breadcrumb.data ?? {})) {
    if (typeof value === 'string' && breadcrumb.data) breadcrumb.data[key] = redactSensitive(value);
  }
  return breadcrumb;
}

export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  if (event.message) event.message = redactSensitive(event.message);
  if (event.request?.url) event.request.url = redactSensitive(event.request.url);
  for (const [key, value] of Object.entries(event.request?.headers ?? {})) {
    if (!event.request?.headers) continue;
    if (SENSITIVE_HEADERS.has(key.toLowerCase())) {
      event.request.headers[key] = '[redacted]';
    } else if (typeof value === 'string') {
      event.request.headers[key] = redactSensitive(value);
    }
  }
  for (const ex of event.exception?.values ?? []) {
    if (ex.value) ex.value = redactSensitive(ex.value);
  }
  const crumbs = event.breadcrumbs;
  if (crumbs) {
    for (let i = crumbs.length - 1; i >= 0; i--) {
      if (scrubBreadcrumb(crumbs[i]) === null) crumbs.splice(i, 1);
    }
  }
  return event;
}

// 4xx is expected app flow (409 research conflict, 403 membership denial,
// 404 invalid dialog token) — only server faults are error-tracker signal.
export function isCapturableHttpStatus(status: number): boolean {
  return status >= 500;
}
